import { useState } from "react";
import { ROOMS, NEXT_OPENING, formatPrice } from "../data/rooms";
import { ArrowRight, ArrowLeft, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const questions = [
  {
    id: "mission",
    question: "What's your main focus right now?",
    options: [
      { label: "Building a startup or product", value: "startup", weight: 95 },
      { label: "Creative project (writing, design, music)", value: "creative", weight: 90 },
      { label: "Freelancing or scaling remote work", value: "freelance", weight: 85 },
      { label: "Taking a reset — figuring out what's next", value: "reset", weight: 80 }
    ]
  },
  {
    id: "environment",
    question: "How much would a distraction-free, sober environment help you right now?",
    options: [
      { label: "Exactly what I need — take my money", value: "10", weight: 100 },
      { label: "I'd benefit from it, yeah", value: "7", weight: 85 },
      { label: "I could take it or leave it", value: "4", weight: 50 },
      { label: "I'd probably struggle with that", value: "2", weight: 25 }
    ]
  },
  {
    id: "duration",
    question: "How long are you thinking?",
    options: [
      { label: "1 month — a focused sprint", value: "1", weight: 80 },
      { label: "2 months — real momentum", value: "2", weight: 95 },
      { label: "3+ months — I'm going deep", value: "3", weight: 100 },
      { label: "Just a couple of weeks", value: "short", weight: 30 }
    ]
  },
  {
    id: "community",
    question: "Shared meals, skill shares, group accountability — your vibe?",
    options: [
      { label: "That's literally why I'm interested", value: "yes", weight: 100 },
      { label: "I'm open to it", value: "open", weight: 80 },
      { label: "I'd join sometimes but mostly keep to myself", value: "sometimes", weight: 60 },
      { label: "I prefer being completely solo", value: "solo", weight: 30 }
    ]
  }
];

const rooms = ROOMS.map((r) => ({ name: r.shortName, price: formatPrice(r.price), note: r.note, popular: r.id === "sky" }));

const STAY_LENGTH_MAP = { "1": "1 month", "2": "2 months", "3": "3+ months", "short": "2 weeks" };

const getPersonalisedNudge = (answers) => {
  const mission = answers.mission?.value;
  const duration = answers.duration?.value;
  const community = answers.community?.value;

  if (mission === "startup") return "Founders usually grab the Sky Room — best light for long sessions.";
  if (mission === "creative") return "The studio is included for residents. Just saying.";
  if (duration === "3") return "Extended stays get priority. Most 3-month guests end up extending.";
  if (community === "yes") return "You'd fit right in. The communal dinners are the highlight for most people.";
  if (duration === "1") return "One month is enough to completely rewire your routine.";
  return "Most guests say the first week changes everything.";
};

const SaviQuiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [firstName, setFirstName] = useState("");
  const [dates, setDates] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const currentQ = questions[step];
  const isQuizDone = step >= questions.length;

  const handleSelect = (option) => {
    const newAnswers = { ...answers, [currentQ.id]: option };
    setAnswers(newAnswers);

    if (step < questions.length - 1) {
      setTimeout(() => setStep(step + 1), 200);
    } else {
      const total = Object.values(newAnswers).reduce((sum, a) => sum + a.weight, 0);
      const avg = Math.round(total / questions.length);
      setScore(avg);
      setStep(questions.length);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    const cleanedPhone = phone.trim().replace(/[\s\-().]/g, "");
    if (cleanedPhone && !/^\+\d{6,15}$/.test(cleanedPhone)) {
      toast.error("Please include your country code (e.g. +34 612 345 678)");
      return;
    }
    setSubmitting(true);
    try {
      await fetch(`${BACKEND_URL}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() })
      });
      await fetch(`${BACKEND_URL}/api/quiz/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email: email.trim(), 
          phone: phone.trim(),
          first_name: firstName.trim(),
          estimated_dates: dates.trim(),
          score, 
          answers: Object.fromEntries(
            Object.entries(answers).map(([k, v]) => [k, v.label])
          ),
          selected_room: selectedRoom
        })
      });
      await fetch(`${BACKEND_URL}/api/waitlist/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          target_dates: dates.trim(),
          room_preference: selectedRoom || "No preference",
          stay_length: STAY_LENGTH_MAP[answers.duration?.value] || ""
        })
      });
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "waitlist_signup", { event_category: "conversion" });
      }
      setEmailSent(true);
    } catch {
      setEmailSent(true);
    }
    setSubmitting(false);
  };

  const getWhatsAppUrl = () => {
    const duration = answers.duration?.value === "1" ? "1 month" : answers.duration?.value === "2" ? "2 months" : "3+ months";
    const focus = answers.mission?.label || "my project";
    const roomText = selectedRoom ? ` I'm interested in the ${selectedRoom}.` : "";
    return `https://wa.me/34632628051?text=Hi%20Steve!%20I%20got%20${score}%25%20on%20the%20Savi%20quiz.%20I'm%20focused%20on%20${encodeURIComponent(focus.toLowerCase())}%20and%20thinking%20about%20${encodeURIComponent(duration)}.${encodeURIComponent(roomText)}%20Would%20love%20to%20chat.`;
  };

  // Quiz questions
  if (!isQuizDone) {
    return (
      <div className="max-w-lg mx-auto" data-testid="savi-quiz">
        <div className="flex gap-1.5 mb-6">
          {questions.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= step ? 'bg-[#C85A38]' : 'bg-[#2E2A26]/10'}`} />
          ))}
        </div>

        <p className="text-[#2E2A26]/30 text-xs mb-2">{step + 1} of {questions.length}</p>
        <h3 className="font-serif text-xl sm:text-2xl text-[#2E2A26] mb-6">{currentQ.question}</h3>

        <div className="space-y-2.5">
          {currentQ.options.map((opt, i) => (
            <button
              key={i}
              onClick={() => handleSelect(opt)}
              className="w-full text-left p-4 rounded-xl border border-stone-200 hover:border-[#C85A38] hover:bg-[#C85A38]/5 transition-all text-sm text-[#2E2A26]/70 hover:text-[#2E2A26]"
              data-testid={`quiz-option-${currentQ.id}-${i}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {step > 0 && (
          <button onClick={() => setStep(step - 1)} className="mt-4 text-[#2E2A26]/30 text-xs hover:text-[#2E2A26]/50 flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
        )}
      </div>
    );
  }

  // Results
  const nudge = getPersonalisedNudge(answers);

  return (
    <div className="max-w-2xl mx-auto" data-testid="quiz-result">
      {/* Score */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#C85A38]/10 mb-4">
          <span className="text-[#C85A38] font-serif text-3xl">{score}%</span>
        </div>
        <h3 className="font-serif text-2xl text-[#2E2A26] mb-2">
          {score >= 85 ? "Sounds like you'd fit right in." : score >= 65 ? "We think you'd enjoy it here." : score >= 45 ? "Could work — worth a chat." : "Maybe not the right moment, and that's OK."}
        </h3>
        <p className="text-[#C85A38] text-sm italic">{nudge}</p>
        {score >= 65 && (
          <div className="mt-4 inline-flex items-center gap-2 bg-[#C85A38]/10 border border-[#C85A38]/20 rounded-full px-4 py-1.5" data-testid="quiz-availability-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C85A38] animate-pulse" />
            <span className="text-[#C85A38] text-xs font-medium tracking-wide">Next room opens {NEXT_OPENING} — winter dates going now</span>
          </div>
        )}
      </div>

      {/* Room Selection */}
      {score >= 45 && (
        <div className="mb-8">
          <p className="text-[#2E2A26]/40 text-xs text-center mb-4 uppercase tracking-wider">Which room catches your eye?</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {rooms.map((room) => (
              <button
                key={room.name}
                onClick={() => setSelectedRoom(room.name)}
                className={`relative text-left p-3.5 rounded-xl border-2 transition-all ${
                  selectedRoom === room.name 
                    ? 'border-[#C85A38] bg-[#C85A38]/5' 
                    : 'border-stone-200 hover:border-[#C85A38]/40'
                }`}
                data-testid={`room-select-${room.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {room.popular && (
                  <span className="absolute -top-2 right-3 bg-[#C85A38] text-white text-[9px] px-2 py-0.5 rounded-full font-medium">Most picked</span>
                )}
                <p className="text-[#2E2A26] text-sm font-medium">{room.name}</p>
                <p className="text-[#2E2A26]/40 text-xs mb-1">{room.note}</p>
                <p className="text-[#C85A38] font-serif text-base">{room.price}<span className="text-[#2E2A26]/20 text-xs font-sans">/mo</span></p>
              </button>
            ))}
          </div>
          {selectedRoom && (
            <p className="text-center text-[#2E2A26]/30 text-xs mt-3">
              Good choice. No commitment — just helps Steve know what you're thinking.
            </p>
          )}
        </div>
      )}

      {/* Email + WhatsApp */}
      {!emailSent ? (
        <div className="text-center">
          <p className="text-[#2E2A26]/40 text-xs mb-4">
            {score >= 65 
              ? "Leave your details and Steve will come back within 24 hours with dates that work. Or just message him now." 
              : "Leave your details anyway — Steve's happy to talk it through, no pressure."}
          </p>
          <form onSubmit={handleEmailSubmit} className="flex flex-col gap-2.5 max-w-md mx-auto mb-4" data-testid="quiz-email-form">
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
              required
              className="bg-white border border-stone-200 rounded-full px-5 py-3 text-sm text-[#2E2A26] placeholder:text-[#2E2A26]/30 focus:outline-none focus:border-[#C85A38]"
              data-testid="quiz-firstname-input"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="bg-white border border-stone-200 rounded-full px-5 py-3 text-sm text-[#2E2A26] placeholder:text-[#2E2A26]/30 focus:outline-none focus:border-[#C85A38]"
              data-testid="quiz-email-input"
            />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="WhatsApp / phone (with country code)"
              className="bg-white border border-stone-200 rounded-full px-5 py-3 text-sm text-[#2E2A26] placeholder:text-[#2E2A26]/30 focus:outline-none focus:border-[#C85A38]"
              data-testid="quiz-phone-input"
            />
            <input
              type="text"
              value={dates}
              onChange={(e) => setDates(e.target.value)}
              placeholder="Target dates (e.g. Oct 1 — Nov 30)"
              className="bg-white border border-stone-200 rounded-full px-5 py-3 text-sm text-[#2E2A26] placeholder:text-[#2E2A26]/30 focus:outline-none focus:border-[#C85A38]"
              data-testid="quiz-dates-input"
            />
            <button
              type="submit"
              disabled={submitting}
              className="bg-[#C85A38] hover:bg-[#A84830] text-white text-sm font-medium rounded-full px-6 py-3 transition-colors disabled:opacity-50"
              data-testid="quiz-email-submit"
            >
              {submitting ? "..." : "Send my details to Steve"}
            </button>
          </form>
          {score >= 65 && (
            <a
              href={getWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#25D366] text-sm font-medium hover:underline"
              data-testid="quiz-whatsapp-link"
            >
              <Sparkles className="w-4 h-4" /> Or message Steve directly on WhatsApp
            </a>
          )}
        </div>
      ) : (
        <div className="text-center" data-testid="quiz-success">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#C85A38]/10 mb-4">
            <Check className="w-7 h-7 text-[#C85A38]" />
          </div>
          <h3 className="font-serif text-2xl text-[#2E2A26] mb-2">
            You're on the list, {firstName.split(" ")[0] || "friend"}.
          </h3>
          <p className="text-[#2E2A26]/60 text-sm max-w-md mx-auto leading-relaxed mb-6">
            Steve's checking your dates and will come back within 24 hours with availability. Keep an eye on your inbox — and WhatsApp, if you left a number.
          </p>
          
          {score >= 65 && (
            <div>
              <a
                href={getWhatsAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white text-sm font-medium rounded-full px-8 py-3.5 transition-colors"
              >
                Skip the wait — chat with Steve now <ArrowRight className="w-4 h-4" />
              </a>
              <p className="text-[#2E2A26]/25 text-xs mt-3">Most people who message get a reply within an hour.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SaviQuiz;
